/**
 * A calendar date window (`yyyy-MM-dd` on both ends) — the shape every date
 * filter surface speaks (the date popup's shortcuts, the pill label, the
 * `startDate`/`endDate` pair the feed queries).
 */
export interface DateRange {
  from: string;
  to: string;
}

/**
 * What the date popup commits (T-UX-FIX): the popup is the single date
 * control, and a choice is either an explicit PERIOD page (`oggi`,
 * `questo-weekend`, a month slug — SEO pages under `/eventi/…/{period}`), a
 * plain RANGE (`settimana`, a custom calendar window) or a CLEAR (back to the
 * default "all upcoming" window). The consumer (`useFilters`) decides whether
 * the period can become a period URL on the current surface and otherwise
 * falls back to the SAME window as `startDate`/`endDate`.
 */
export type DateSelection =
  | { kind: 'clear' }
  | { kind: 'range'; from: string; to: string }
  | { kind: 'period'; slug: string; from: string; to: string };

export function parseCalendarDate(dateString: string): Date {
  const parts = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T ])/.exec(dateString);
  if (!parts) return new Date(dateString);

  // Event dates are calendar days: ignore an optional time and timezone suffix.
  return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
}

export function formatDate(dateString: string): string {
  const date = parseCalendarDate(dateString);
  return date.toLocaleDateString('it-IT', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

export function formatDateRange(startDate: string, endDate?: string): string {
  if (!endDate || startDate === endDate) {
    return formatDate(startDate);
  }
  const start = parseCalendarDate(startDate);
  const end = parseCalendarDate(endDate);

  // Same month: show compact range
  if (start.getMonth() === end.getMonth()) {
    return `${start.getDate()} - ${end.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}`;
  }

  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
}
